import React, { useEffect, useState } from 'react';
import { Loader2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getGoogleSigninConfig, updateGoogleSigninConfig } from '@/services/admin';
import AutojoinActivityLog from './AutojoinActivityLog';

const GoogleSigninConfig = ({ tenantId }) => {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getGoogleSigninConfig(tenantId).then(setCfg).catch(() => toast.error('Failed to load Google sign-in settings'));
  }, [tenantId]);

  if (!cfg) return <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" data-testid="google-signin-loading" />;

  const addDomain = () => {
    const d = draft.trim().toLowerCase().replace(/^@/, '');
    if (d && !cfg.domains.includes(d)) setCfg({ ...cfg, domains: [...cfg.domains, d] });
    setDraft('');
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateGoogleSigninConfig(tenantId, {
        enabled: cfg.enabled, domains: cfg.domains, default_role: cfg.default_role,
      });
      setCfg(res);
      toast.success('Google sign-in settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="google-signin-config">
      <p className="text-sm text-muted-foreground">
        Users you've already invited can always sign in with Google. Turn this on to let new people with a
        company email address join this tenant automatically the first time they sign in with Google.
      </p>
      <div className="flex items-center justify-between rounded-lg border p-3">
        <Label htmlFor="google-autojoin" className="font-medium">Auto-join by email domain</Label>
        <Switch id="google-autojoin" checked={cfg.enabled}
          onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="google-autojoin-switch" />
      </div>
      <div className="space-y-2">
        <Label>Company email domains</Label>
        <div className="flex gap-2">
          <Input value={draft} placeholder="acme.com" onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
            data-testid="google-domain-input" />
          <Button type="button" variant="outline" onClick={addDomain} data-testid="google-domain-add-btn">Add</Button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="google-domain-list">
          {cfg.domains.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 rounded-full border bg-slate-50 px-3 py-1 text-xs">
              @{d}
              <button type="button" aria-label={`Remove ${d}`} data-testid={`google-domain-remove-${d}`}
                onClick={() => setCfg({ ...cfg, domains: cfg.domains.filter((x) => x !== d) })}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {!cfg.domains.length && <span className="text-xs text-muted-foreground">No domains yet</span>}
        </div>
      </div>
      <div className="space-y-2 max-w-xs">
        <Label>Role for auto-joined users</Label>
        <Select value={cfg.default_role} onValueChange={(v) => setCfg({ ...cfg, default_role: v })}>
          <SelectTrigger data-testid="google-default-role-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {cfg.allowed_roles.map((r) => (
              <SelectItem key={r} value={r} data-testid={`google-role-option-${r}`}>{r.replace('_', ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button onClick={save} disabled={saving} data-testid="google-signin-save-btn">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save
      </Button>
      <AutojoinActivityLog tenantId={tenantId} />
    </div>
  );
};

export default GoogleSigninConfig;
